import {Subject} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MetadataTypeSearchTerm, MetadataTypeService} from '../../services/api/metadata-type.service';
import {MetadataType, MetadataTypeId} from '@app/graphql-typings/graphql';
import {AceSelectComponent} from '../ace-select/ace-select.component';
import {MessageTranslateService} from '@shared/services';
import {I18nMessage} from '@app/shared/api/model/i18n';
import {ArrayUtils, CollectionUtils} from '@app/shared/util/collections/collection-utils';

export type MetadataTypesSelectSearchTerm = Pick<MetadataTypeSearchTerm, 'type_ids'>;


@Component({
  selector: 'app-metadata-types-select',
  templateUrl: './metadata-types-select.component.html',
  styleUrls: ['./metadata-types-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetadataTypesSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class MetadataTypesSelectComponent implements OnInit, OnChanges, ControlValueAccessor {

  private readonly MAX_ITEMS = 3;
  private metadataTypesById: Map<MetadataTypeId, MetadataType>;
  private allMetadataTypes: MetadataType[] | null = null;

  selectedMetadataTypes: number[] | MetadataType[];

  @Input() metadataTypes: MetadataType[];
  @Input() optionsSearchTerm: MetadataTypesSelectSearchTerm | null = null;
  @Input() id: string;
  @Input() required: boolean;
  @Input() class: string;
  @Input() multiSelect = false;
  @Input() placeholder: string;
  @Input() maxSelectedLabels = this.MAX_ITEMS;
  @Input() maxSelectedShowItemName = true;
  @Input() maxSelectedItemName: I18nMessage = {messageKey: 'types'};
  @Input() truncateLabels = false;

  @Input() appendTo: string;
  @Input() bindToTypeId = false;
  @Input() loading: boolean;
  @Input() hideSelected = true;
  @Input() clearable = true;
  /* selectCommissionable can have three states;
 * - null - All types (commissionable & non-commissionable) will be shown
 * - true - Only commissionable types will be shown
 * - false - Only non-commissionable types will be shown */
  @Input() selectCommissionable: boolean = null;
  @Input() tooltipById: Map<MetadataTypeId, I18nMessage> | null = null;

  @Output() dataLoaded = new EventEmitter<MetadataType[]>();
  @Output() openEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() closeEvent: EventEmitter<any> = new EventEmitter<any>();

  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  @ViewChild('aceSelect', {static: true})
  private aceSelect: AceSelectComponent;

  private onChange: (_) => void = (_) => {};
  private onTouched: () => void = () => {};

  constructor(private metadataTypeService: MetadataTypeService,
              private translateService: MessageTranslateService) {
  }

  ngOnInit() {
    if (!this.metadataTypes) {
      this.loadMetadataTypesAsync();
    }

    if (this.placeholder === undefined) {
      this.placeholder = this.translateService.instant('Select Metadata Type');
    }

    this.aceSelect.registerOnChange(this.aceSelectChange);
    this.aceSelect.registerOnTouched(this.aceSelectTouched);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // when metadataTypes are set by caller, assume filtering is already done by caller, use provided metadata types verbatim
    if (changes.metadataTypes) {
      this.metadataTypesById = CollectionUtils.indexBy(this.metadataTypes, 'type_id');
      this.adjustSelectedMetadataTypes();
    }
    if (changes.optionsSearchTerm && this.allMetadataTypes) {
      this.filterMetadataTypes();
    }
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.aceSelect.setDisabledState(isDisabled);
  }

  onOpen() {
    this.openEvent.emit();
  }

  onClose() {
    this.closeEvent.emit();
  }

  aceSelectChange = (selected: number | number[] | MetadataType | MetadataType[]) => {
    this.selectedMetadataTypes = this.toSelectedArray(selected);
    this.onChange(selected);
  };

  aceSelectTouched = () => {
    this.onTouched();
  };

  writeValue(value: any): void {
    this.selectedMetadataTypes = this.toSelectedArray(value);
    this.aceSelect.writeValue(value);
  }

  compareFn(a: MetadataType, b: MetadataType): boolean {
    return a.type_id === b.type_id;
  }

  private toSelectedArray(value: number | number[] | MetadataType | MetadataType[]): number[] | MetadataType[] | null {

    if (value == null) {
      return null;
    }

    if (Array.isArray(value)) {
      return value;
    }

    return <number[] | MetadataType[]> [value];
  }

  private loadMetadataTypesAsync() {
    this.loading = true;
    this.metadataTypeService.getAllMetadataTypes({commissionable: this.selectCommissionable})
      .pipe(finalize(() => this.loading = false))
      .subscribe(metadataTypes => {
        this.allMetadataTypes = metadataTypes;
        // emit the full catalog so consumers can label any type; the displayed options are scoped below
        this.dataLoaded.emit(metadataTypes);
        this.filterMetadataTypes();
      });
  }

  private filterMetadataTypes(): void {
    const matchesSearchTerm = this.buildMetadataTypeFilter(this.optionsSearchTerm);
    const metadataTypes: MetadataType[] = [];
    const metadataTypesById = new Map<MetadataTypeId, MetadataType>();
    (this.allMetadataTypes ?? []).forEach(metadataType => {
      if (matchesSearchTerm(metadataType)) {
        metadataTypes.push(metadataType);
        metadataTypesById.set(metadataType.type_id, metadataType);
      }
    });
    this.metadataTypes = metadataTypes;
    this.metadataTypesById = metadataTypesById;
    this.adjustSelectedMetadataTypes();
  }

  private buildMetadataTypeFilter(searchTerm: MetadataTypesSelectSearchTerm | null): (metadataType: MetadataType) => boolean {
    const predicates: ((metadataType: MetadataType) => boolean)[] = [];
    if (searchTerm?.type_ids?.length) {
      const typeIdSet = new Set<MetadataTypeId>(searchTerm.type_ids);
      predicates.push(metadataType => typeIdSet.has(metadataType.type_id));
    }
    return predicates.length
      ? metadataType => predicates.every(predicate => predicate(metadataType))
      : () => true;
  }

  private adjustSelectedMetadataTypes(): void {
    const selectedValue = this.selectedMetadataTypes;
    if (selectedValue) {
      const selectedValueArray = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
      const newValues = this.bindToTypeId
        ? (<MetadataTypeId[]>selectedValueArray).filter(id => this.metadataTypesById.has(id))
        // Using map to ensure the selected values (objects) are the ones from the this.metadataTypesById
        : (<MetadataType[]>selectedValueArray).map(item => this.metadataTypesById.get(item.type_id)).filter(e => e != null);
      if (!ArrayUtils.containSameItems<MetadataTypeId | MetadataType>(selectedValueArray ?? [], newValues ?? [])) {
        const value = newValues.length ? (this.multiSelect ? newValues : newValues[0]) : null;
        this.writeValue(value);
        this.onChange(value);
      }
    }
  }

  // Must be implemented as an arrow function when passed as an @Input parameter
  // Regular functions lose their parent component's 'this' context when executed in child components.
  getTooltip = (item: MetadataType): I18nMessage | null => {
    return item?.type_id !== null
      ? this.tooltipById?.get(item.type_id) ?? null
      : null;
  };
}
